import { ApolloClient, Reference } from '@apollo/client';

import {
  ProjectListNodeFragment,
  ProjectListNodeFragmentDoc,
  ProjectMemberRole,
  ProjectSortOption,
} from '@graphql/generated';

// Mirrors every keyArg declared on the `listProjects` field policy
// (initializeCache.ts) — that field is shared by many callers beyond
// AllProjects (project maps, task/activity filters, contact/user project
// tabs, etc.), so all of them can show up in a cached storeFieldName here.
type ListProjectsArgs = {
  archivesOnly?: boolean;
  ownerIds?: string[];
  memberIds?: string[];
  teamIds?: string[];
  role?: ProjectMemberRole;
  sortBy?: ProjectSortOption;
  filterTeamsProjects?: boolean;
  locationOnly?: boolean;
  chatId?: string;
  userId?: string;
};

// Best-effort, case-insensitive approximation of the backend's NAME_ASC
// collation — close enough to keep the cached list roughly in order between
// refetches; exact tie-breaking on punctuation/unicode is left to the next
// natural refetch rather than chased here.
const compareByName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

const findNameAscInsertIndex = (
  edges: { node: Reference }[],
  name: string,
  readField: (fieldName: string, from: Reference) => unknown
) => {
  let i = 0;
  for (; i < edges.length; i++) {
    const otherName = readField('name', edges[i].node) as string | undefined;
    if (otherName !== undefined && compareByName(name, otherName) < 0) break;
  }
  return i;
};

// relayStylePagination's own read() only derives startCursor/endCursor from
// the edges when the stored pageInfo cursor is empty (pagination.ts) — it
// never overwrites an already-populated one. So whenever we mutate `edges`
// here, pageInfo has to be kept in sync ourselves, or a stale endCursor
// (e.g. pointing at an edge we just removed) survives every subsequent read
// and gets handed straight to fetchFromCursor's `after` argument.
//
// A listProjects cursor is an opaque, server-issued token — never a project
// id — and a real fetchMore({ after }) call sends whatever pageInfo.endCursor
// holds straight to the server. The edge this module inserts has no such
// token (see the `cursor: null` comment below), so pageInfo must be derived
// from the first/last edge that carries a *real* (non-null) cursor, skipping
// over any synthetic edges entirely — otherwise a synthetic edge landing at
// either boundary would leak its placeholder into pageInfo and break the
// next real pagination request.
type EdgeWithCursor = { cursor?: string | null };
const withEdges = <T extends { pageInfo?: Record<string, unknown> }>(
  existing: T,
  edges: EdgeWithCursor[]
) => {
  const realCursors = edges
    .map((edge) => edge.cursor)
    .filter((cursor): cursor is string => cursor != null && cursor !== '');
  return {
    ...existing,
    edges,
    pageInfo: {
      ...existing.pageInfo,
      startCursor: realCursors[0] ?? '',
      endCursor: realCursors[realCursors.length - 1] ?? '',
    },
  };
};

// cache.modify's field-function details don't include the field's `args`
// (Apollo only ever supplies fieldName/storeFieldName/readField/toReference —
// see EntityStore.prototype.modify in @apollo/client/cache/inmemory/entityStore.js).
// listProjects' keyArgs policy (initializeCache.ts) encodes the args it cares
// about directly into storeFieldName as `listProjects:{"archivesOnly":false,...}`,
// so recover them by parsing that suffix instead.
const parseStoreFieldNameArgs = (
  storeFieldName: string,
  fieldName: string
): ListProjectsArgs => {
  const prefix = `${fieldName}:`;
  if (!storeFieldName.startsWith(prefix)) return {};
  try {
    return JSON.parse(storeFieldName.slice(prefix.length));
  } catch {
    return {};
  }
};

// Patches a project update pushed by the projectListNodeUpdated subscription
// (create/update/archive/unarchive — project delete is a separate,
// currently-unwired subscription, see TA-10206 PR body) directly into the
// Apollo cache instead of triggering a full listProjects refetch.
export const patchProjectListCache = (
  client: ApolloClient<object>,
  project: ProjectListNodeFragment
) => {
  const { cache } = client;
  const projectCacheId = cache.identify({
    __typename: 'Project',
    id: project.id,
  });
  if (!projectCacheId) return;

  // Updates every list/detail view that already holds a reference to this
  // project — normalization means this one write reaches all of them.
  cache.writeFragment({
    id: projectCacheId,
    fragment: ProjectListNodeFragmentDoc,
    fragmentName: 'ProjectListNode',
    data: project,
  });

  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      listProjects(
        existing,
        { readField, toReference, fieldName, storeFieldName }
      ) {
        if (!existing?.edges) return existing;

        const args = parseStoreFieldNameArgs(storeFieldName, fieldName);

        const ref = toReference({ __typename: 'Project', id: project.id });
        if (!ref) return existing;

        const index = existing.edges.findIndex(
          (edge: { node: Reference }) =>
            readField('id', edge.node) === project.id
        );

        const archivesOnly = !!args?.archivesOnly;
        const belongsToArchiveState = !!project.archivedAt === archivesOnly;
        // We can only re-derive a variant's ordering client-side for NAME_ASC
        // (the fragment carries `name`). Other sort orders — e.g. recentProjects'
        // ACTIVITY_DESC, which is also capped via `first` — can't be safely
        // repositioned or grown from this payload, so those variants are left
        // untouched by the reorder/insert logic below and fall back to their
        // next natural refetch.
        const canReorder = args?.sortBy === ProjectSortOption.NameAsc;

        if (index !== -1) {
          // Already in this variant — drop it if archiving/unarchiving just
          // moved it out of this variant's archivesOnly filter.
          if (!belongsToArchiveState) {
            const edges = existing.edges.slice();
            edges.splice(index, 1);
            return withEdges(existing, edges);
          }

          if (!canReorder) return existing;

          // Still belongs here, but a name change may have moved it out of
          // alphabetical order — reposition rather than leaving it in place.
          const edgesWithoutIt = existing.edges.slice();
          const [edge] = edgesWithoutIt.splice(index, 1);
          const insertAt = findNameAscInsertIndex(
            edgesWithoutIt,
            project.name,
            readField
          );
          if (insertAt === index) return existing;
          edgesWithoutIt.splice(insertAt, 0, edge);
          return withEdges(existing, edgesWithoutIt);
        }

        if (!belongsToArchiveState) return existing;
        if (!canReorder) return existing;

        // Only auto-insert into variants whose filters we can verify against
        // the fragment we have (role, teamIds). Every other listProjects
        // keyArg narrows by something the fragment doesn't carry enough to
        // check — ownerIds/memberIds need a full membership list,
        // filterTeamsProjects/locationOnly/userId/chatId need server-side
        // context this cache patch doesn't have — so those filtered variants
        // are left for their next natural refetch rather than risking a
        // project appearing where it doesn't belong.
        const hasUnverifiableFilter =
          (args?.ownerIds?.length ?? 0) > 0 ||
          (args?.memberIds?.length ?? 0) > 0 ||
          !!args?.filterTeamsProjects ||
          !!args?.locationOnly ||
          !!args?.chatId ||
          !!args?.userId;
        if (hasUnverifiableFilter) return existing;
        if (args?.role && args.role !== project.currentUserRole)
          return existing;
        if (
          (args?.teamIds?.length ?? 0) > 0 &&
          !args?.teamIds?.includes(project.teamId)
        )
          return existing;

        const edges = existing.edges.slice();
        const insertAt = findNameAscInsertIndex(edges, project.name, readField);
        edges.splice(insertAt, 0, {
          __typename: 'ProjectEdge',
          // cursor must be present (an edge missing the field entirely, as
          // opposed to carrying an explicit value, makes Apollo see this
          // cached connection as incomplete and silently re-fetch it over
          // the network, undoing the point of this patch) — but it must be
          // `null`, not a synthesized value like the project id, so it can
          // never be mistaken for a real server cursor when pageInfo is
          // derived in withEdges above.
          cursor: null,
          node: ref,
        });
        return withEdges(existing, edges);
      },
    },
  });
};
